import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'

import { identifyBrowser } from './utils/datadogUtil'

// Benign Chrome/Firefox warnings emitted when a ResizeObserver callback triggers
// another resize within the same animation frame. The browser self-corrects, no
// user-facing impact, but Datadog captures them and inflates the error count.
// See https://github.com/WICG/resize-observer/issues/38
const RESIZE_OBSERVER_NOISE = /ResizeObserver loop (completed with undelivered notifications|limit exceeded)/i

const EXTENSION_FRAME_PROTOCOLS = [
  'chrome-extension',
  'moz-extension',
  'safari-web-extension',
  'safari-extension',
]

const isResizeObserverNoise = (message: unknown): boolean =>
  typeof message === 'string' && RESIZE_OBSERVER_NOISE.test(message)

const isBrowserExtensionNoise = (event: { error?: { stack?: unknown; message?: unknown } }): boolean => {
  const stack = typeof event.error?.stack === 'string' ? event.error.stack : ''
  const message = typeof event.error?.message === 'string' ? event.error.message : ''
  const haystack = `${stack} ${message}`
  return EXTENSION_FRAME_PROTOCOLS.some((proto) => haystack.includes(proto))
}

export enum MonitoringApp {
  SPARK = 'SPARK',
  REPROGRAMMING = 'REPROGRAMMING',
  FLEET_TRIAGE = 'FLEET_TRIAGE',
}

interface ConfigEntry {
  envs: string[]
  rootPath: string
  propagatorTypes?: ('datadog' | 'tracecontext' | 'b3' | 'b3multi')[]
}

type ConfigsMap = {
  [K in keyof typeof MonitoringApp as K]: ConfigEntry
}

interface SetupMonitoringOptions {
  env: {
    CURRENT_ENVIRONMENT: string
    VITE_DATADOG_APP_ID: string
    VITE_DATADOG_TOKEN: string
  }
  appName: string
  appType: MonitoringApp
}

const hostname = window.location.hostname

const configs: ConfigsMap = {
  SPARK: {
    envs: ['staging-sandbox', 'staging', 'prod'],
    rootPath: `https://${hostname}.spark.ai/int`,
  },
  REPROGRAMMING: {
    envs: ['staging', 'prod'],
    rootPath: `https://${hostname}/int`,
  },
  FLEET_TRIAGE: {
    envs: ['staging', 'prod'],
    rootPath: `https://${hostname}`,
    propagatorTypes: ['tracecontext'],
  },
}

export async function setupMonitoring({ env, appName, appType }: SetupMonitoringOptions) {
  const shouldEnableMonitoring = configs[appType].envs.includes(env.CURRENT_ENVIRONMENT)

  if (!shouldEnableMonitoring) {
    console.info('Monitoring is disabled.')
    return
  }

  const browser = await identifyBrowser()

  const datadogSite = 'datadoghq.com'

  datadogLogs.init({
    clientToken: env.VITE_DATADOG_TOKEN,
    site: datadogSite,
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
    service: appName,
    env: env.CURRENT_ENVIRONMENT,
    version: __CURRENT_RELEASE__,
    beforeSend: (log) => {
      if (isResizeObserverNoise(log.message)) return false
      if (isBrowserExtensionNoise({ error: { stack: log.error?.stack, message: log.message } })) return false
      return true
    },
  })

  datadogRum.init({
    applicationId: env.VITE_DATADOG_APP_ID,
    clientToken: env.VITE_DATADOG_TOKEN,
    service: appName,
    site: datadogSite,
    defaultPrivacyLevel: 'mask-user-input',
    env: env.CURRENT_ENVIRONMENT,
    version: __CURRENT_RELEASE__,
    sessionSampleRate: 100,
    traceSampleRate: 100,
    sessionReplaySampleRate: 100,
    allowedTracingUrls: [
      configs[appType].propagatorTypes
        ? {
            match: (url: string) => url.startsWith(configs[appType].rootPath),
            propagatorTypes: configs[appType].propagatorTypes!,
          }
        : (url: string) => url.startsWith(configs[appType].rootPath),
    ],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    beforeSend: (event, context: any) => {
      if (event.type === 'error') {
        if (isResizeObserverNoise(event.error.message)) return false
        if (isBrowserExtensionNoise(event)) return false
      }

      try {
        if (event.type === 'resource' && ['xhr'].includes(event.resource.type)) {
          const validResponse =
            context.xhr.response === '' || context.xhr.response === undefined ? '{}' : context.xhr.response

          event.context = {
            ...event.context,
            response: JSON.parse(validResponse),
          }
        }
      } catch {
        return true
      }

      return true
    },
  })

  datadogRum.setGlobalContextProperty('browserName', browser)
}
